import {
  API_BASE_URL,
  getAccountScopedLocalStorageKey,
  getAuthHeaders,
  setLocalStorageWithSync,
} from './utils';

// Premium users with extended voice minutes limits
// Add email addresses here to grant higher weekly limits
const PREMIUM_VOICE_USERS = {
  // Format: 'email@example.com': { weeklyMinutes: 120, maxSessionMinutes: 15 }
  // Example entries:
  // 'john.doe@example.com': { weeklyMinutes: 120, maxSessionMinutes: 15 },
  // 'jane.smith@example.com': { weeklyMinutes: 180, maxSessionMinutes: 20 },
  'patrick.sanang@gmail.com': { weeklyMinutes: 100, maxSessionMinutes: 9 },
  'nguessiromaric@outlook.fr': { weeklyMinutes: 100, maxSessionMinutes: 9 },
  'simoyvesloic18@gmail.com': { weeklyMinutes: 100, maxSessionMinutes: 9 },
  'christianheumou@yahoo.fr': { weeklyMinutes: 100, maxSessionMinutes: 9 },
};

// ============================================================================
// JOURNEY DAY OVERRIDE - Advance users to specific days in their learning journey
// ============================================================================
// Add email addresses here to override their journey day
// Format: 'email@example.com': dayNumber (1-60)
// The user's journey will appear as if they are on that day
const JOURNEY_DAY_OVERRIDES = {
  // Examples:
  // 'john.doe@example.com': 15,  // Advance to day 15
  // 'jane.smith@example.com': 30, // Advance to day 30
  'patrick.sanang@gmail.com': 30, // Advance Patrick to day 30
  'leopoldineandja@gmail.com': 30, // Advance Leopoldine to day 30
};

export const getJourneyDayOverrideStorageKey = (userEmail) => {
  if (!userEmail) return null;
  return `journey_day_override_${String(userEmail).trim().toLowerCase()}`;
};

export const getVoiceLimitsStorageKey = (userEmail) => {
  if (!userEmail) return null;
  return `voice_limits_override_${String(userEmail).trim().toLowerCase()}`;
};

const readStoredJson = (key) => {
  if (!key) return null;
  try {
    const rawValue = localStorage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    console.error(`Error parsing localStorage key ${key}:`, error);
    return null;
  }
};

const VOICE_ACTIVE_SESSION_KEY = 'voiceActiveSession';

const getVoiceActiveSessionStorageKey = () =>
  getAccountScopedLocalStorageKey(VOICE_ACTIVE_SESSION_KEY);

const getVoiceActiveSessionItem = () => {
  const key = getVoiceActiveSessionStorageKey();
  return key ? localStorage.getItem(key) : null;
};

const setVoiceActiveSessionItem = (value) => {
  const key = getVoiceActiveSessionStorageKey();
  if (!key) return false;
  localStorage.setItem(key, value);
  return true;
};

const removeVoiceActiveSessionItem = () => {
  const key = getVoiceActiveSessionStorageKey();
  if (key) localStorage.removeItem(key);
};

/**
 * Get the journey day override for a specific user
 * @param {string} userEmail - User's email address
 * @returns {number|null} - Override day number, or null if no override
 */
export const getJourneyDayOverride = (userEmail) => {
  if (!userEmail) return null;
  const email = userEmail.toLowerCase();
  const storedOverride = readStoredJson(getJourneyDayOverrideStorageKey(email));

  if (typeof storedOverride === 'number' && Number.isFinite(storedOverride)) {
    return storedOverride;
  }

  if (
    storedOverride &&
    typeof storedOverride.day === 'number' &&
    Number.isFinite(storedOverride.day)
  ) {
    return storedOverride.day;
  }

  return JOURNEY_DAY_OVERRIDES[email] || null;
};

/**
 * Check if a user has a journey day override
 * @param {string} userEmail - User's email address
 * @returns {boolean} - True if user has an override
 */
export const hasJourneyDayOverride = (userEmail) => {
  if (!userEmail) return false;
  const email = userEmail.toLowerCase();
  return (
    readStoredJson(getJourneyDayOverrideStorageKey(email)) !== null ||
    email in JOURNEY_DAY_OVERRIDES
  );
};

export const getSpeakingPracticeMinutes = () => {
  const minutes = Number(getUserVoiceLimits().MAX_SESSION_MINUTES);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 9;
};

// Get current user's voice limits (checks for premium status)
export const getUserVoiceLimits = () => {
  try {
    const user = JSON.parse(
      sessionStorage.getItem('user') || localStorage.getItem('user') || '{}'
    );
    const email = user?.email?.toLowerCase();

    const storedLimits = readStoredJson(getVoiceLimitsStorageKey(email));
    if (storedLimits && Number.isFinite(Number(storedLimits.maxSessionMinutes))) {
      const maxSessionMinutes = Number(storedLimits.maxSessionMinutes);
      const maxSessionsPerDay = Number.isFinite(
        Number(storedLimits.maxSessionsPerDay)
      )
        ? Number(storedLimits.maxSessionsPerDay)
        : 5;

      return {
        WEEKLY_MINUTES: Number(storedLimits.weeklyMinutes) || 0,
        MAX_SESSION_MINUTES: maxSessionMinutes,
        MAX_SESSIONS_PER_DAY: maxSessionsPerDay,
        MAX_SESSIONS_PER_WEEK: maxSessionsPerDay * 7,
        IS_PREMIUM: true,
      };
    }
  } catch (error) {
    console.error('Error checking premium status:', error);
  }

  return {
    WEEKLY_MINUTES: 0,
    MAX_SESSION_MINUTES: Number(
      process.env.REACT_APP_VOICE_MAX_SESSION_MINUTES || 9
    ),
    MAX_SESSIONS_PER_DAY: 5,
    MAX_SESSIONS_PER_WEEK: 5,
    IS_PREMIUM: false,
  };
};

// Constants for voice session management (default values, use getUserVoiceLimits() for actual limits)
export const VOICE_LIMITS = {
  WEEKLY_MINUTES: 0,
  MAX_SESSION_MINUTES: Number(
    process.env.REACT_APP_VOICE_MAX_SESSION_MINUTES || 9
  ),
  MAX_SESSIONS_PER_DAY: 5,
  MAX_SESSIONS_PER_WEEK: 5,
};

// Check if user is premium
export const isPremiumVoiceUser = () => {
  return getUserVoiceLimits().IS_PREMIUM;
};

// Get start of current week (Monday)
export const getWeekStart = () => {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
  const monday = new Date(now.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return monday;
};

// Get week display text (e.g., "For week of Dec 9 to Dec 15, 2024")
export const getCurrentWeekDisplay = () => {
  const weekStart = getWeekStart();
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  const formatDate = (date) => {
    const options = { month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const startDate = formatDate(weekStart);
  const endDate = formatDate(weekEnd);
  const year = weekStart.getFullYear();

  return `For week of ${startDate} to ${endDate}, ${year}`;
};

// Get current week key for storage
export const getCurrentWeekKey = () => {
  const weekStart = getWeekStart();
  return `week_${weekStart.getTime()}`;
};

// Initialize weekly usage data structure
export const initializeWeeklyUsage = () => {
  return {
    weekKey: getCurrentWeekKey(),
    totalMinutesUsed: 0,
    sessions: [],
    createdAt: new Date().toISOString(),
  };
};

// Get current weekly usage from localStorage
export const getWeeklyUsage = async () => {
  const currentWeekKey = getCurrentWeekKey();
  const stored = localStorage.getItem('voice_minutes_data');

  if (!stored) {
    const newUsage = initializeWeeklyUsage();
    await setLocalStorageWithSync(
      'voice_minutes_data',
      JSON.stringify(newUsage)
    );
    return newUsage;
  }

  const usage = JSON.parse(stored);

  // If it's a new week, reset the usage
  if (usage.weekKey !== currentWeekKey) {
    const newUsage = initializeWeeklyUsage();
    await setLocalStorageWithSync(
      'voice_minutes_data',
      JSON.stringify(newUsage)
    );
    return newUsage;
  }

  return usage;
};

let dailyAllowanceCache = { value: null, fetchedAt: 0 };

export const resetDailyVoiceAllowanceCache = () => {
  dailyAllowanceCache = { value: null, fetchedAt: 0 };
};

export const fetchDailyVoiceAllowance = async ({ force = false } = {}) => {
  const now = Date.now();
  if (
    !force &&
    dailyAllowanceCache.value &&
    now - dailyAllowanceCache.fetchedAt < 15000
  ) {
    return dailyAllowanceCache.value;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/voice/daily-allowance`, {
      headers: getAuthHeaders(),
      cache: 'no-store',
    });
    if (!response.ok) {
      return dailyAllowanceCache.value;
    }
    const data = await response.json();
    dailyAllowanceCache = { value: data, fetchedAt: now };
    return data;
  } catch (error) {
    console.error('Failed to load daily voice allowance:', error);
    return dailyAllowanceCache.value;
  }
};

export const getRemainingMinutes = async () => {
  const allowance = await fetchDailyVoiceAllowance();
  if (Number.isFinite(Number(allowance?.sessionsRemaining))) {
    return Number(allowance.sessionsRemaining);
  }
  return getUserVoiceLimits().MAX_SESSIONS_PER_DAY || 5;
};

export const getMaxSessionDuration = async () => {
  const allowance = await fetchDailyVoiceAllowance();
  const fromAllowance = Number(allowance?.maxSessionMinutes);
  if (Number.isFinite(fromAllowance) && fromAllowance > 0) {
    return fromAllowance;
  }
  return getUserVoiceLimits().MAX_SESSION_MINUTES || 9;
};

export const canStartSession = async () => {
  const allowance = await fetchDailyVoiceAllowance();
  if (!allowance) {
    return true;
  }
  return allowance.canStart !== false;
};

// Record a completed session
export const recordSession = async (
  durationMinutes,
  sessionType = 'speaking'
) => {
  const usage = await getWeeklyUsage();

  const newSession = {
    id: Date.now(),
    type: sessionType, // 'speaking' or 'interview'
    duration: durationMinutes,
    startTime: new Date().toISOString(),
    endTime: new Date().toISOString(),
  };

  usage.sessions.push(newSession);
  usage.totalMinutesUsed += durationMinutes;

  await setLocalStorageWithSync('voice_minutes_data', JSON.stringify(usage));

  return usage;
};

// Get session statistics
export const getSessionStats = async () => {
  const usage = await getWeeklyUsage();
  const activeSession = getActiveSession();
  const limits = getUserVoiceLimits();

  let adjustedTotalMinutesUsed = usage.totalMinutesUsed;

  // If there's an active session, include its current usage
  if (activeSession) {
    const elapsedMinutes = getActiveSessionElapsedTime() / 60;
    adjustedTotalMinutesUsed += elapsedMinutes;
  }

  const allowance = await fetchDailyVoiceAllowance();
  const remainingSessions =
    allowance?.sessionsRemaining ?? limits.MAX_SESSIONS_PER_DAY ?? 5;
  const maxSessionsPerDay =
    allowance?.maxSessionsPerDay ?? limits.MAX_SESSIONS_PER_DAY ?? 5;

  return {
    totalMinutesUsed: adjustedTotalMinutesUsed,
    remainingMinutes: remainingSessions,
    remainingSessions,
    sessionsUsedToday: allowance?.sessionsUsed ?? 0,
    maxSessionsPerDay,
    weeklyLimit: maxSessionsPerDay,
    maxSessionLimit: limits.MAX_SESSION_MINUTES,
    isPremium: limits.IS_PREMIUM,
    sessionsCount: allowance?.sessionsUsed ?? usage.sessions.length,
    maxSessionDuration: await getMaxSessionDuration(),
    canStartNewSession: (await canStartSession()) && !activeSession,
    sessions: usage.sessions,
    activeSession: activeSession,
  };
};

// Format minutes for display
export const formatMinutes = (minutes) => {
  if (minutes < 1) {
    return `${Math.round(minutes * 60)} secs`;
  }
  const wholeMinutes = Math.floor(minutes);
  const seconds = Math.round((minutes - wholeMinutes) * 60);

  if (seconds === 0) {
    return `${wholeMinutes}min`;
  }
  return `${wholeMinutes}min ${seconds} secs`;
};

export const formatSessionMinutesLabel = (minutes) => {
  if (minutes < 1) {
    return `${Math.round(minutes * 60)}s left`;
  }

  const wholeMinutes = Math.floor(minutes);
  const seconds = Math.round((minutes - wholeMinutes) * 60);

  if (minutes >= 60) {
    return `${Math.round(minutes)}m left`;
  }

  if (seconds === 0) {
    return `${wholeMinutes}m left`;
  }

  return `${wholeMinutes}m ${seconds}s left`;
};

// Convert seconds to minutes (for session tracking)
export const secondsToMinutes = (seconds) => {
  return Math.round((seconds / 60) * 100) / 100; // Round to 2 decimal places
};

// Active session management
export const startActiveSession = async (sessionType = 'speaking') => {
  const activeSession = {
    id: Date.now(),
    type: sessionType,
    startTime: new Date().toISOString(),
    maxDuration: await getMaxSessionDuration(),
    isActive: true,
    totalPausedMs: 0,
    pausedAt: null,
  };

  setVoiceActiveSessionItem(JSON.stringify(activeSession));
  return activeSession;
};

export const pauseActiveSession = () => {
  const stored = getVoiceActiveSessionItem();
  if (!stored) return null;

  try {
    const activeSession = JSON.parse(stored);
    if (!activeSession.isActive || activeSession.pausedAt) {
      return activeSession;
    }

    activeSession.totalPausedMs = activeSession.totalPausedMs || 0;
    activeSession.pausedAt = new Date().toISOString();
    setVoiceActiveSessionItem(JSON.stringify(activeSession));
    return activeSession;
  } catch (error) {
    console.error('Error pausing active session:', error);
    return null;
  }
};

export const resumeActiveSession = () => {
  const stored = getVoiceActiveSessionItem();
  if (!stored) return null;

  try {
    const activeSession = JSON.parse(stored);
    if (!activeSession.isActive || !activeSession.pausedAt) {
      return activeSession;
    }

    const pausedMs =
      Date.now() - new Date(activeSession.pausedAt).getTime();
    activeSession.totalPausedMs =
      (activeSession.totalPausedMs || 0) + Math.max(0, pausedMs);
    activeSession.pausedAt = null;
    setVoiceActiveSessionItem(JSON.stringify(activeSession));
    return activeSession;
  } catch (error) {
    console.error('Error resuming active session:', error);
    return null;
  }
};

const getActiveSessionElapsedMs = (activeSession, now = new Date()) => {
  const startTime = new Date(activeSession.startTime);
  let elapsedMs = now.getTime() - startTime.getTime();
  const totalPausedMs = activeSession.totalPausedMs || 0;

  if (activeSession.pausedAt) {
    const currentPauseMs =
      now.getTime() - new Date(activeSession.pausedAt).getTime();
    elapsedMs -= totalPausedMs + Math.max(0, currentPauseMs);
  } else {
    elapsedMs -= totalPausedMs;
  }

  return Math.max(0, elapsedMs);
};

export const getActiveSession = () => {
  const stored = getVoiceActiveSessionItem();
  if (!stored) return null;

  try {
    const session = JSON.parse(stored);
    if (!session.isActive) return null;

    // Check if session is stale (older than max session duration + buffer)
    // If so, it's likely from a crashed/reloaded page and shouldn't count
    const now = new Date();
    const elapsedMinutes = getActiveSessionElapsedMs(session, now) / (1000 * 60);
    const maxDuration = session.maxDuration || getSpeakingPracticeMinutes();

    // If elapsed time exceeds max duration by more than 2 minutes, consider it stale
    // A legitimate session would have been ended by the timer
    if (elapsedMinutes > maxDuration + 2) {
      console.log(
        'Detected stale session (exceeded max duration):',
        elapsedMinutes,
        'minutes'
      );
      // Clear stale session without recording usage
      clearStaleSession();
      return null;
    }

    return session;
  } catch (error) {
    console.error('Error parsing active session:', error);
    return null;
  }
};

// Clear a stale session without recording usage (for crashed/reloaded pages)
export const clearStaleSession = () => {
  const stored = getVoiceActiveSessionItem();
  if (!stored) return null;

  try {
    const session = JSON.parse(stored);
    console.log('Clearing stale session without recording usage:', {
      sessionId: session.id,
      type: session.type,
      startTime: session.startTime,
    });

    removeVoiceActiveSessionItem();
    return session;
  } catch (error) {
    console.error('Error clearing stale session:', error);
    removeVoiceActiveSessionItem();
    return null;
  }
};

export const updateActiveSessionProgress = (minutesUsed) => {
  const activeSession = getActiveSession();
  if (!activeSession) return null;

  activeSession.currentMinutesUsed = minutesUsed;
  activeSession.lastUpdated = new Date().toISOString();

  setVoiceActiveSessionItem(JSON.stringify(activeSession));
  return activeSession;
};

export const endActiveSession = async (options = {}) => {
  const { skipRecording = false, maxRecordMinutes = null } = options;

  // Get session directly from localStorage to avoid stale check clearing it
  const stored = getVoiceActiveSessionItem();
  if (!stored) {
    console.log('No active session to end');
    return null;
  }

  let activeSession;
  try {
    activeSession = JSON.parse(stored);
    if (!activeSession.isActive) {
      console.log('Session is not active');
      removeVoiceActiveSessionItem();
      return null;
    }
  } catch (error) {
    console.error('Error parsing active session:', error);
    removeVoiceActiveSessionItem();
    return null;
  }

  const endTime = new Date();
  let actualDurationMinutes =
    getActiveSessionElapsedMs(activeSession, endTime) / (1000 * 60);

  // Cap the duration at the max session duration to prevent stale sessions
  // from consuming more than they should
  const maxDuration = activeSession.maxDuration || 9;
  if (actualDurationMinutes > maxDuration) {
    console.log(
      `Capping session duration from ${actualDurationMinutes} to ${maxDuration} minutes`
    );
    actualDurationMinutes = maxDuration;
  }

  // If maxRecordMinutes is specified, cap at that value
  if (maxRecordMinutes !== null && actualDurationMinutes > maxRecordMinutes) {
    console.log(
      `Capping session duration to maxRecordMinutes: ${maxRecordMinutes}`
    );
    actualDurationMinutes = maxRecordMinutes;
  }

  console.log('Ending active session:', {
    sessionId: activeSession.id,
    type: activeSession.type,
    duration: actualDurationMinutes,
    startTime: activeSession.startTime,
    endTime: endTime.toISOString(),
    skipRecording,
  });

  // Record the completed session (unless skipped for interrupted sessions)
  if (!skipRecording && actualDurationMinutes > 0) {
    await recordSession(actualDurationMinutes, activeSession.type);
  } else {
    console.log('Skipping session recording (interrupted or zero duration)');
  }

  // Clear active session - ensure it's removed
  try {
    removeVoiceActiveSessionItem();
    console.log('Active session removed from localStorage');
  } catch (error) {
    console.error('Error removing active session from localStorage:', error);
  }

  return {
    ...activeSession,
    endTime: endTime.toISOString(),
    actualDuration: actualDurationMinutes,
    wasRecorded: !skipRecording && actualDurationMinutes > 0,
  };
};

export const getActiveSessionElapsedTime = () => {
  const activeSession = getActiveSession();
  if (!activeSession) return 0;

  return Math.floor(getActiveSessionElapsedMs(activeSession) / 1000);
};

export const getRemainingSessionTime = () => {
  const activeSession = getActiveSession();
  if (!activeSession) return 0;

  const elapsedSeconds = getActiveSessionElapsedTime();
  const maxSeconds = activeSession.maxDuration * 60;
  const remainingSeconds = Math.max(0, maxSeconds - elapsedSeconds);

  return remainingSeconds;
};

// Backend sync functions
export const syncUsageToBackend = async (usage) => {
  try {
    const user = JSON.parse(
      sessionStorage.getItem('user') || localStorage.getItem('user')
    );
    if (!user) return false;

    const response = await fetch(`${API_BASE_URL}/api/voice-usage/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_email: user.email,
        weekKey: usage.weekKey,
        totalMinutesUsed: usage.totalMinutesUsed,
        sessions: usage.sessions,
      }),
    });

    return response.ok;
  } catch (error) {
    console.error('Failed to sync usage to backend:', error);
    return false;
  }
};

export const fetchUsageFromBackend = async () => {
  try {
    const user = JSON.parse(
      localStorage.getItem('user') || sessionStorage.getItem('user')
    );
    if (!user) return null;

    const currentWeekKey = getCurrentWeekKey();
    const response = await fetch(
      `${API_BASE_URL}/api/voice-usage/${user.email}/${currentWeekKey}`
    );

    if (response.ok) {
      const data = await response.json();
      return data;
    }
    return null;
  } catch (error) {
    console.error('Failed to fetch usage from backend:', error);
    return null;
  }
};

// Subscription upgrade helper
export const getSubscriptionUpgradeInfo = () => {
  return {
    currentPlan: 'Basic',
    upgradeOptions: [
      {
        name: 'Premium',
        weeklyMinutes: 120, // 2 hours per week
        maxSessionMinutes: 15,
        price: '$19.99/month',
        features: [
          '120 minutes per week',
          '15 minutes per session',
          'Unlimited sessions',
          'Advanced feedback',
          'Priority support',
        ],
      },
      {
        name: 'Pro',
        weeklyMinutes: 300, // 5 hours per week
        maxSessionMinutes: 30,
        price: '$39.99/month',
        features: [
          '300 minutes per week',
          '30 minutes per session',
          'Unlimited sessions',
          'Detailed analytics',
          'Custom AI coaching',
          'Premium support',
        ],
      },
    ],
  };
};

export const getTimeUntilReset = () => {
  const now = new Date();
  const localDate = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Douala',
  }).format(now);
  const [year, month, day] = localDate.split('-').map(Number);
  const nextMidnightUtc = Date.UTC(year, month - 1, day + 1, 0, 0, 0) - 3600000;
  const diffMs = Math.max(0, nextMidnightUtc - now.getTime());
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) {
    return `Resets in ${hours}h ${minutes}m`;
  }
  return `Resets in ${minutes}m`;
};
